import { useEffect, useState } from "react";
import { Alert, Form, Input } from "antd";
import AppButton from "../../../../components/ui/AppButton";

const LENGTH = 6;
const OTP_TTL_SECONDS = 5 * 60;

/**
 * OtpStep — step 2 of the forgot-password wizard.
 *
 * Uses antd's Input.OTP for the 6-cell widget (auto-advance, paste, focus
 * management all handled by the primitive). Owns the 5-min countdown and
 * surfaces resend cooldown / attempts-remaining via inline alerts.
 */
export default function OtpStep({
  email,
  submitting,
  resending,
  resendCooldown,
  errorMessage,
  attemptsRemaining,
  onSubmit,
  onResend,
  onBack,
  resetSignal,
}) {
  const [otp, setOtp] = useState("");
  const [remaining, setRemaining] = useState(OTP_TTL_SECONDS);

  useEffect(() => {
    setOtp("");
    setRemaining(OTP_TTL_SECONDS);
  }, [resetSignal]);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  const handleSubmit = async () => {
    if (otp.length !== LENGTH) return;
    await onSubmit(otp);
  };

  const complete = otp.length === LENGTH;
  const expired = remaining <= 0;

  return (
    <Form layout="vertical" onFinish={handleSubmit} requiredMark={false} className="mt-8 space-y-4">
      <p className="text-sm text-mahogany">
        We sent a 6-digit code to <b>{email}</b>. The code expires in{" "}
        <b>{formatSeconds(remaining)}</b>.
      </p>

      <div className="flex justify-center">
        <Input.OTP
          length={LENGTH}
          value={otp}
          onChange={(value) => setOtp((value || "").replace(/\D/g, "").slice(0, LENGTH))}
          formatter={(str) => (str || "").replace(/\D/g, "")}
          disabled={submitting || expired}
          aria-label="One-time password"
          size="large"
        />
      </div>

      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={
            attemptsRemaining != null && attemptsRemaining > 0
              ? `${errorMessage} ${attemptsRemaining} attempt${attemptsRemaining === 1 ? "" : "s"} remaining.`
              : errorMessage
          }
          role="alert"
        />
      )}

      {expired && (
        <Alert type="warning" showIcon message="Code expired. Please resend a new code." />
      )}

      <AppButton
        htmlType="submit"
        type="primary"
        size="large"
        block
        loading={submitting}
        disabled={submitting || !complete || expired}
        label="Verify code"
      />

      <div className="flex items-center justify-between">
        <AppButton
          type="link"
          size="small"
          onClick={onBack}
          disabled={submitting}
          label="Change email"
        />
        <AppButton
          type="link"
          size="small"
          onClick={onResend}
          loading={resending}
          disabled={resending || resendCooldown > 0}
          label={resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
        />
      </div>
    </Form>
  );
}

function formatSeconds(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
